/**
 * Hero background video — picks desktop or mobile URL in JS (reliable on iOS).
 */
(function () {
  const MOBILE_MAX_WIDTH = 749;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function shouldSkipForConnection() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return false;
    if (connection.saveData) return true;
    return ['slow-2g', '2g'].includes(connection.effectiveType);
  }

  function isMobileViewport() {
    return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
  }

  class BannerBackgroundVideo {
    constructor(container) {
      this.container = container;
      this.video = container.querySelector('.banner__video');
      this.poster = container.querySelector('.banner__video-poster');
      this.desktopUrl = container.dataset.desktopVideoUrl || '';
      this.mobileUrl = container.dataset.mobileVideoUrl || '';
      this.mobileBehavior = container.dataset.mobileBehavior || 'no_video';
      this.activeUrl = '';
      this.destroyed = false;
      this.revealed = false;
      this.inView = true;

      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.onVideoReady = this.onVideoReady.bind(this);
      this.onPosterTransitionEnd = this.onPosterTransitionEnd.bind(this);
      this.onIntersect = this.onIntersect.bind(this);

      this.mobileQuery = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
      this.init();
    }

    resolveVideoUrl() {
      if (isMobileViewport()) {
        if (this.mobileBehavior === 'use_mobile_video' && this.mobileUrl) {
          return this.mobileUrl;
        }
        return '';
      }

      return this.desktopUrl;
    }

    shouldSkipPlayback() {
      if (prefersReducedMotion() || shouldSkipForConnection()) return true;
      if (isMobileViewport() && this.mobileBehavior !== 'use_mobile_video') return true;
      return !this.resolveVideoUrl();
    }

    init() {
      if (!this.video) return;

      this.mobileQuery.addEventListener('change', this.onMediaChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.video.addEventListener('loadeddata', this.onVideoReady);
      this.video.addEventListener('canplay', this.onVideoReady);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(this.onIntersect, { threshold: 0.2 });
        this.io.observe(this.container);
      }

      // Poster first. The 1080p file is 4–8 MB; starting it on first paint
      // is what makes the homepage and both drawers hitch.
      const start = () => {
        if (this.destroyed) return;
        this.loadVideoForViewport();
      };
      const idle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 1200));
      if (document.readyState === 'complete') idle(start, { timeout: 2000 });
      else window.addEventListener('load', () => idle(start, { timeout: 2000 }), { once: true });
    }

    onIntersect(entries) {
      this.inView = entries.some((entry) => entry.isIntersecting);
      this.syncPlayback();
    }

    shouldHoldPlayback() {
      if (document.hidden || !this.inView) return true;
      if (document.documentElement.classList.contains('drawer-open')) return true;
      // Inside the hero slideshow, only the visible slide's video may run.
      // component-home-hero.js calls play() when the slide becomes active.
      const slide = this.container.closest('[data-hero-slide]');
      if (slide && !slide.classList.contains('is-active')) return true;
      return false;
    }

    syncPlayback() {
      if (this.destroyed || !this.video || this.shouldSkipPlayback()) return;
      if (this.shouldHoldPlayback()) {
        this.video.pause();
        return;
      }
      if (this.video.paused && this.activeUrl) this.attemptPlay();
    }

    loadVideoForViewport() {
      if (this.destroyed || !this.video) return;

      if (this.shouldSkipPlayback()) {
        this.disableVideo();
        return;
      }

      const url = this.resolveVideoUrl();
      if (!url) {
        this.disableVideo();
        return;
      }

      if (url === this.activeUrl && this.revealed && !this.video.paused) {
        return;
      }

      this.hideVideoUntilReady();
      this.activeUrl = url;
      this.ensureMuted();
      this.video.src = url;
      // Buffer without decoding so a held video (hidden slide) starts
      // instantly when its slide becomes active.
      this.video.preload = 'auto';
      this.video.load();
      this.syncPlayback();

      if (this.video.readyState >= 2) {
        this.onVideoReady();
      }
    }

    hideVideoUntilReady() {
      this.revealed = false;
      this.container.classList.remove('is-video-ready', 'is-poster-hidden');
      this.video.classList.remove('is-ready');
    }

    onVideoReady() {
      if (this.destroyed || this.revealed || !this.video) return;
      if (this.video.readyState < 2) return;

      const reveal = () => {
        if (this.destroyed || this.revealed) return;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.destroyed || this.revealed) return;

            this.revealed = true;
            this.video.classList.add('is-ready');
            this.container.classList.add('is-video-ready');

            if (this.poster) {
              this.poster.addEventListener('transitionend', this.onPosterTransitionEnd);
            } else {
              this.container.classList.add('is-poster-hidden');
            }
          });
        });
      };

      if (!this.video.paused && this.video.currentTime > 0) {
        reveal();
        return;
      }

      this.video.addEventListener('playing', reveal, { once: true });
    }

    onPosterTransitionEnd(event) {
      if (event.propertyName !== 'opacity') return;
      this.container.classList.add('is-poster-hidden');
      if (this.poster) {
        this.poster.removeEventListener('transitionend', this.onPosterTransitionEnd);
      }
    }

    ensureMuted() {
      try {
        if (navigator.audioSession) navigator.audioSession.type = 'ambient';
      } catch (error) {}
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.volume = 0;
      this.video.playsInline = true;
      this.video.disableRemotePlayback = true;
      this.video.setAttribute('playsinline', '');
      this.video.setAttribute('webkit-playsinline', '');
      this.video.setAttribute('muted', '');
    }

    attemptPlay() {
      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    onVisibilityChange() {
      this.syncPlayback();
    }

    onMediaChange() {
      if (this.destroyed) return;
      this.loadVideoForViewport();
    }

    disableVideo() {
      if (!this.video) return;

      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      this.activeUrl = '';
      this.hideVideoUntilReady();
    }

    destroy() {
      this.destroyed = true;
      this.mobileQuery.removeEventListener('change', this.onMediaChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      if (this.io) this.io.disconnect();
      if (this.video) {
        this.video.removeEventListener('loadeddata', this.onVideoReady);
        this.video.removeEventListener('canplay', this.onVideoReady);
      }
      this.disableVideo();
    }
  }

  function initBannerBackgroundVideo(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-hero-video]').forEach((container) => {
      if (container._heroVideoController) {
        container._heroVideoController.destroy();
      }
      container._heroVideoController = new BannerBackgroundVideo(container);
    });
  }

  function destroyBannerBackgroundVideo(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-hero-video]').forEach((container) => {
      if (container._heroVideoController) {
        container._heroVideoController.destroy();
        delete container._heroVideoController;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initBannerBackgroundVideo(document));
  } else {
    initBannerBackgroundVideo(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initBannerBackgroundVideo(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyBannerBackgroundVideo(event.target);
  });
})();
